import { z } from "zod";

const statusSchema = z.object({
  available: z.boolean(),
  knowledge_version: z.string().nullable(),
  support_url: z.string().nullable(),
  mode: z.enum(["published", "preview", "disabled"]),
});

const answerSchema = z.object({
  answer: z.string().min(1).max(30000),
  citations: z.array(z.object({
    section_id: z.string(),
    document_id: z.string(),
    title: z.string(),
    heading: z.string(),
    excerpt: z.string().max(20000),
    verified_at: z.string(),
  })).max(12),
  knowledge_version: z.string().nullable(),
  requires_confirmation: z.boolean(),
  escalation_required: z.boolean(),
  outcome: z.enum(["answered", "confirmation_required", "out_of_scope", "insufficient_evidence"]),
  request_id: z.string().min(1).max(128),
});

export type SupportStatus = Omit<z.infer<typeof statusSchema>, "mode"> & {
  mode: "published" | "preview" | "disabled" | "reviewer_preview";
};
export type SupportAnswer = z.infer<typeof answerSchema>;
export type SupportRating = "helpful" | "not_helpful";
export interface SupportChatClient {
  getStatus(signal: AbortSignal): Promise<SupportStatus>;
  ask(question: string, signal: AbortSignal): Promise<SupportAnswer>;
  feedback(requestId: string, rating: SupportRating, signal: AbortSignal): Promise<void>;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL?.trim() || "").replace(/\/$/, "");
export const SUPPORT_REQUEST_TIMEOUT_MS = 30000;

// This public, product-only client never reads the CRM session or browser storage.
async function request(path: string, signal: AbortSignal, body?: unknown): Promise<unknown> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener("abort", abort, { once: true });
  if (signal.aborted) controller.abort();
  let timedOut = false;
  const timeout = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, SUPPORT_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_BASE}/api/support-chat/${path}`, {
      method: body === undefined ? "GET" : "POST",
      headers: body === undefined ? { Accept: "application/json" } : { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "omit",
      cache: "no-store",
      signal: controller.signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!response.ok) {
      if (response.status === 429) throw new Error("Too many questions right now. Please wait a moment and try again.");
      if (response.status === 503) throw new Error("Product help is temporarily unavailable. Please try again later.");
      throw new Error("We could not complete your request. Please try again.");
    }
    return await response.json();
  } catch (error) {
    if (timedOut) throw new Error("The request took too long. Please try again.");
    if (signal.aborted) throw error;
    if (error instanceof Error && !["TypeError", "SyntaxError"].includes(error.name)) throw error;
    throw new Error("We could not connect to product help. Check your connection and try again.");
  } finally {
    window.clearTimeout(timeout);
    signal.removeEventListener("abort", abort);
  }
}

export async function getSupportStatus(signal: AbortSignal): Promise<SupportStatus> {
  return statusSchema.parse(await request("status", signal));
}

export async function askSupport(question: string, signal: AbortSignal): Promise<SupportAnswer> {
  const payload = await request("answer", signal, { question, locale: "en" });
  return parseSupportAnswer(payload);
}

export function parseSupportAnswer(payload: unknown): SupportAnswer {
  const result = answerSchema.safeParse(payload);
  if (!result.success) throw new Error("Product help returned an unreadable answer. Please try again.");
  return result.data;
}

export async function sendSupportFeedback(requestId: string, rating: SupportRating, signal: AbortSignal): Promise<void> {
  const result = await request("feedback", signal, { request_id: requestId, rating });
  parseSupportFeedback(result);
}

export function parseSupportFeedback(result: unknown): void {
  if (!z.object({ accepted: z.literal(true) }).safeParse(result).success) {
    throw new Error("Your feedback could not be saved. Please try again.");
  }
}

export const publicSupportClient: SupportChatClient = {
  getStatus: getSupportStatus,
  ask: askSupport,
  feedback: sendSupportFeedback,
};

export function safeSupportUrl(value: string | null): string | null {
  if (!value || Array.from(value).some((character) => character.charCodeAt(0) < 32)) return null;
  try {
    const url = new URL(value);
    if (url.protocol === "https:" && !url.username && !url.password) return url.href;
    if (url.protocol === "mailto:" && /^[^\s?<>]+@[^\s?<>]+$/.test(url.pathname) && !url.search && !url.hash) return url.href;
  } catch { /* Unusable support configuration must not become a customer link. */ }
  return null;
}
